import { ErrorBoundary } from "@sentry/react";
import FeatureAttachments from "components/feature-attachments";
import { ProjectPermissionEnum } from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import { TFetureDetailUpdateAction } from "entity-app/models";
import EditableDetailsCardWrapper from "entity-app/shared-components/editable-details-card-wrapper/editable-details-card-wrapper";

import SubmittalLinkedTopSection from "pages/submittal-details/new-submittal-details/components/submittal-linked-top-section";
import { useContext, useMemo } from "react";
import { useParams } from "react-router";
import { Link } from "react-router-dom";
import MaterialGeneralInfoCard from "./material-general-info";
import FeatureMaterialParticipants from "./material-participants";
import MaterialCharacteristicsCard from "./material-characteristics";
import { MaterialDetailLinks } from "./material-details.links";
import FeatureMaterialCommentsHistory from "./material-comments-history";
import "./feature-material-details.scss";

export default function MaterialDetailCards(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  featureId: string;
}) {
  const { featureInstanceData, actions, featureId } = props;
  const { projectId } = useParams() as any;
  const {
    tokenContents,
    columnHeaders: { materialHeaderMap }
  }: TProjectContext = useContext(ProjectContext);

  const canInlineEditMaterialGeneralInfoAndParticipants: any =
    isPermissionNotGrantted(
      ProjectPermissionEnum.EditMaterialGeneralInfoAndParticipants,
      tokenContents?.role!
    );

  const canInlineEditMaterialCharacteristics: any = isPermissionNotGrantted(
    ProjectPermissionEnum.EditMaterialCharacteristics,
    tokenContents?.role!
  );

  const canEditLinkingPage = !isPermissionNotGrantted(
    ProjectPermissionEnum.EditLinkingPage,
    tokenContents?.role!
  );

  const material = useMemo(() => {
    if (!featureInstanceData?.feature_materials?.length) return null;
    return featureInstanceData?.feature_materials[0];
  }, [featureInstanceData?.feature_materials]);

  return (
    <div
      className="flex flex-col p-4 space-y-4 feature-material-details"
      data-testid="material-detail-cards-root"
    >
      <SubmittalLinkedTopSection
        actions={actions}
        featureInstanceData={featureInstanceData}
        data-testid="material-detail-cards-linked-top-section"
      />
      <div
        className="grid grid-cols-12 gap-4"
        data-testid="material-detail-cards-grid"
      >
        <div
          className="col-span-4 flex flex-col space-y-4"
          data-testid="material-detail-cards-col-general"
        >
          <EditableDetailsCardWrapper
            title="General Information"
            data-testid="material-detail-cards-general-wrapper"
          >
            <ErrorBoundary>
              <MaterialGeneralInfoCard
                featureInstanceData={featureInstanceData}
                actions={actions}
                disabled={canInlineEditMaterialGeneralInfoAndParticipants}
                projectId={projectId}
                materialHeaderMap={materialHeaderMap}
                data-testid="material-detail-cards-general-info"
              />
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
          <EditableDetailsCardWrapper
            title="Participants"
            data-testid="material-detail-cards-participants-wrapper"
          >
            <ErrorBoundary>
              <FeatureMaterialParticipants
                featureInstanceData={featureInstanceData}
                actions={actions}
                disabled={canInlineEditMaterialGeneralInfoAndParticipants}
                projectId={projectId}
                materialHeaderMap={materialHeaderMap}
                tokenContents={tokenContents}
                data-testid="material-detail-cards-participants"
              />
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
          <EditableDetailsCardWrapper
            title="Material Characteristics"
            data-testid="material-detail-cards-characteristics-wrapper"
          >
            <ErrorBoundary>
              <MaterialCharacteristicsCard
                featureInstanceData={featureInstanceData}
                actions={actions}
                disabled={canInlineEditMaterialCharacteristics}
                materialHeaderMap={materialHeaderMap}
                material={material}
                data-testid="material-detail-cards-characteristics"
              />
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
        </div>
        <div
          className="col-span-4 flex flex-col comments-history"
          data-testid="material-detail-cards-col-comments"
        >
          <EditableDetailsCardWrapper
            title="Comments & History"
            data-testid="material-detail-cards-comments-wrapper"
          >
            <ErrorBoundary>
              <FeatureMaterialCommentsHistory
                featureInstanceData={featureInstanceData}
                tokenContents={tokenContents}
                data-testid="material-detail-cards-comments"
              />
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
        </div>
        <div
          className="col-span-4 flex flex-col space-y-4"
          data-testid="material-detail-cards-col-links-attachments"
        >
          <EditableDetailsCardWrapper
            title="Links"
            data-testid="material-detail-cards-links-wrapper"
            extra={
              canEditLinkingPage ? (
                <Link
                  to={`/project/${projectId}/schedule/material-linking?&materialId=${featureId}`}
                  target="_blank"
                  data-testid="material-detail-cards-links-manage-link"
                >
                  Manage
                </Link>
              ) : null
            }
          >
            <ErrorBoundary>
              <MaterialDetailLinks
                featureInstanceData={featureInstanceData}
                canEditLinkingPage={canEditLinkingPage}
                data-testid="material-detail-cards-links"
              />
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
          <EditableDetailsCardWrapper
            title="Attachments"
            data-testid="material-detail-cards-attachments-wrapper"
          >
            <ErrorBoundary>
              <div
                className="h-[582px] overflow-y-auto"
                data-testid="material-detail-cards-attachments-scroll"
              >
                <FeatureAttachments
                  featureId={featureId}
                  featureKey="feature_instance_id"
                  data-testid="material-detail-cards-attachments"
                />
              </div>
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
        </div>
      </div>
    </div>
  );
}
